/**
 * The Ghana phone rule for a hospital's emergency line (M35-T05), mirroring
 * the server's `backend/app/lib/phone.py`, which stays the real guard. Keep the
 * two in step.
 *
 * Stricter than `dialablePhone`, which accepts any country's 9-15 digits and
 * once let a fake number through (`lib/amtc.ts`):
 *
 *   +233 and 9 digits, or 0 and 9 digits; `00233…` and `+233 0…` too.
 *   Spaces, dashes, dots and brackets are ignored. ASCII digits only — an
 *   Arabic-Indic or fullwidth number is refused, as on the server.
 */

const SEPARATORS = /[\s\-.()]/g
const INTERNATIONAL = /^(?:\+|00)2330?([1-9][0-9]{8})$/
const NATIONAL = /^0([1-9][0-9]{8})$/

/** Right shape, not real. `+233302000000` once shipped as a dead button. */
export const KNOWN_FAKES: ReadonlySet<string> = new Set([
  '+233302000000',
  '+233000000000',
])

export type GhanaPhoneResult =
  | { ok: true; normalised: string }
  | { ok: false; message: string }

export function checkGhanaPhone(raw: string): GhanaPhoneResult {
  const compact = raw.trim().replace(SEPARATORS, '')
  const match = INTERNATIONAL.exec(compact) ?? NATIONAL.exec(compact)
  if (!match) {
    return {
      ok: false,
      message:
        'Enter a Ghana number: +233 and 9 digits, or 0 and 9 digits (e.g. 0302 123 456).',
    }
  }
  const normalised = `+233${match[1]}`
  if (KNOWN_FAKES.has(normalised)) {
    return {
      ok: false,
      message: 'That number is a known placeholder, not a real line.',
    }
  }
  return { ok: true, normalised }
}
